import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils"

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default: "bg-primary text-primary-foreground hover:bg-primary/90 shadow-sm transition-smooth",
        destructive:
          "bg-destructive text-destructive-foreground hover:bg-destructive/90 shadow-sm transition-smooth",
        outline:
          "border border-input bg-background hover:bg-accent hover:text-accent-foreground transition-smooth",
        secondary:
          "bg-secondary text-secondary-foreground hover:bg-secondary/80 shadow-sm transition-smooth",
        ghost: "hover:bg-accent hover:text-accent-foreground transition-smooth",
        link: "text-primary underline-offset-4 hover:underline transition-smooth",
        hero: "bg-gradient-primary text-primary-foreground hover:bg-primary-dark shadow-glow transition-smooth",
        agro: "bg-gradient-secondary text-secondary-foreground hover:bg-secondary/90 shadow-sm transition-smooth",
        success: "bg-success text-success-foreground hover:bg-success/90 shadow-sm transition-smooth",
        warning: "bg-warning text-warning-foreground hover:bg-warning/90 shadow-sm transition-smooth",
      },
      size: {
        default: "h-10 px-4 py-2",
        sm: "h-9 rounded-md px-3",
        lg: "h-11 rounded-md px-8",
        icon: "h-10 w-10",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button"
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    )
  }
)
Button.displayName = "Button"

export { Button, buttonVariants }
